'use client';

import { getTimeSign } from '@/api/public';
import { ICartItem } from '@/api/types/cart';
import Button from '@/components/Button';
import Dialog from '@/components/Dialog';
import { useReporting } from '@/hooks/useReporting';
import { useStore } from '@/providers/app-store-provider';
import { ProviderType } from '@/reporting';
import logger from '@/utils/client-logger';
import { IS_FR } from '@/utils/constants';
import { env } from '@/utils/env';
import { formatPriceNumberWithSymbol } from '@/utils/price';
import { getRouteCnName } from '@/utils/route-metadata';
import { useDebounceFn } from 'ahooks';
import { useTranslations } from '@/i18n/use-translations';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';

interface HalfPriceDialogProps {
  selectedCanBuy: ICartItem[];
  batchFlag: boolean;
  currency: any;
  publicHalfPrice: any;
  onClose?: () => void;
}

export default function HalfPriceDialog({
  selectedCanBuy,
  batchFlag,
  currency,
  publicHalfPrice,
  onClose,
}: HalfPriceDialogProps) {
  const t = useTranslations('cart.halfPriceDialog');
  const [halfPriceShow, setHalfPriceShow] = useState(false);
  const showLoginRegisterDialog = useStore((state) => state.showLoginRegisterDialog);
  const quickCouponDialog = useStore((state) => state.quickCouponDialog);

  // 获取页面信息用于埋点
  const pathname = usePathname();
  const currentPageCnName = getRouteCnName(pathname);
  const { trackEvent } = useReporting();

  // 获取时间标记
  const fetchTimeSign = async () => {
    try {
      const { data } = await getTimeSign({
        type: 'halfPriceDialog',
        second: publicHalfPrice.rule.popup_interval,
      });
      return (data as any)?.exist ?? 1;
    } catch (error) {
      logger.error('获取时间标记失败:', error);
      return 1;
    }
  };

  // 检查是否显示弹窗
  const checkVisible = async () => {
    // 满足以下条件的用户进入购物车时，购物车展示第二件半价弹窗
    // 1.判断勾选的商品数量为1个，眼镜镜框价格大于等于门槛价格
    // 2.读取接口缓存时间  展示弹窗
    // 3. 非编辑模式下，展示弹窗
    const filterResult = selectedCanBuy.filter((item) => item.product?.is_glass === 1); // 镜框的条目

    // 如果第二件半价活动未开启，则不展示弹窗
    if (publicHalfPrice.rule.popup_state !== 1) return;
    const { threshold_price } = publicHalfPrice.rule;

    if (filterResult.length === 1 && !batchFlag) {
      // 勾选的镜框条目为1
      const { product: { is_glass, price_info = [] } = {}, qty } = filterResult[0];
      const usPrice =
        price_info.find((item) => item.currency === env.NEXT_PUBLIC_DEFAULT_CURRENCY) || {}; // 美元币种的价格

      if (
        qty === 1 && // 当前勾选的购物车条目 商品数量为1
        is_glass === 1 && // 是否镜框 1是 2否
        Number(usPrice.sell_price) >= Number(threshold_price) && // 镜框价格 大于等于 第二件半价门槛
        !quickCouponDialog.show && // 优惠券弹窗未显示
        !showLoginRegisterDialog // 登录弹窗未显示
      ) {
        const exist = await fetchTimeSign();
        if (exist === 0) {
          setHalfPriceShow(true);
          SecondHalfPopUpExp();
        }
      }
    }
  };

  // 埋点统计
  const SecondHalfPopUpExp = () => {
    try {
      const eventParams = {
        page_name: currentPageCnName,
        url_path: window.location.href,
        popup_type: '第二件半价提醒弹窗',
      };

      trackEvent(
        {
          eventName: 'SecondHalfPopUp_Exp',
          properties: eventParams,
        },
        [ProviderType.SENSORS],
      );

      logger.log('SecondHalfPopUp_Exp 埋点发送成功', eventParams);
    } catch (error) {
      logger.error('埋点失败:', error);
    }
  };

  // 关闭弹窗
  const close = () => {
    setHalfPriceShow(false);
    onClose?.();
  };

  // 防抖处理
  const { run: debouncedCheckVisible } = useDebounceFn(checkVisible, { wait: 2000 });

  // 监听selectedCanBuy变化
  useEffect(() => {
    debouncedCheckVisible();
  }, [selectedCanBuy, debouncedCheckVisible]);

  // 初始检查
  useEffect(() => {
    debouncedCheckVisible();
  }, [debouncedCheckVisible]);

  // 监听showLoginRegisterDialog和quickCouponDialog.show变化
  useEffect(() => {
    if (showLoginRegisterDialog || quickCouponDialog.show) {
      close();
    }
  }, [showLoginRegisterDialog, quickCouponDialog.show]);

  if (!publicHalfPrice?.rule) {
    return null;
  }

  return (
    <Dialog
      open={halfPriceShow}
      onClose={close}
      className="half-price-dialog"
      showCloseIcon
      footer={null}
      sx={{
        '& .MuiDialog-paper': {
          width: '600px',
          '@media (max-width: 600px)': {
            width: '320px',
          },
        },
        '& .MuiDialogContent-root': {
          '@media (max-width: 600px)': {
            paddingBottom: '12px',
          },
        },
        '& .MuiDialogTitle-root': {
          background: 'linear-gradient(180deg, rgba(186,172,255,0.4) 0%, rgba(222,214,255,0) 100%)',
          padding: '18px 0',
          '@media (min-width: 600px)': {
            background: 'none',
            padding: '32px 40px 24px 40px',
          },
        },
      }}
    >
      <div className="flex flex-col items-center">
        {/* 这边 fr 的内容展示有所区别，并不是翻译变化 */}
        {/* https://devops.aliyun.com/projex/project/ce85697916dd382a20b8106373/req/b8123c1cab9553898e6a3c6b15 */}
        {IS_FR ? (
          <>
            <div className="text-[13px] sm:text-[26px] font-bb text-center">Profitez de</div>
            <div className="text-[48px] sm:text-[49px] font-bb text-primary-purple leading-[49px] mt-[4px] sm:mt-[8px] tracking-[-1px]">
              {publicHalfPrice.rule.discount_rate}% {t('off')}
            </div>
            <div className="text-[12px] sm:text-[24px] font-bb text-center mt-[10px]">
              sur votre 2ème monture
            </div>
            <div className="text-[12px] sm:text-[16px] text-center mt-[10px] sm:mt-[22px] text-[#808080]">
              Offre limitée : maximum {publicHalfPrice.rule.restrict_single} paires par commande
            </div>
          </>
        ) : (
          <>
            <div className="text-[13px] sm:text-[26px] font-bb text-center">{t('wannaEnjoy')}</div>

            <div className="text-[48px] sm:text-[49px] font-bb text-primary-purple leading-[49px] mt-[4px] sm:mt-[8px] tracking-[-1px]">
              {publicHalfPrice.rule.discount_rate}% {t('off')}
            </div>

            <div className="text-[12px] sm:text-[24px] font-bb text-center mt-[10px]">
              {t('yourSecondFrame')}
            </div>

            <div className="text-[12px] sm:text-[16px] text-center mt-[10px] sm:mt-[22px] text-[#808080]">
              {t('notValidUnder')}{' '}
              {formatPriceNumberWithSymbol(publicHalfPrice.rule.threshold_price, currency)}
            </div>

            <div className="text-[12px] sm:text-[16px] text-center mt-[1px] sm:mt-[3px] text-[#808080]">
              {t('eachOrderPaired')} {publicHalfPrice.rule.restrict_single} {t('pairs')}
            </div>
          </>
        )}

        <Button
          variant="contained"
          className="w-full sm:w-[300px] h-[43px] sm:h-[50px] !text-[14px] !sm:text-[18px] !font-bb !mt-[24px] sm:!mt-[30px]"
          onClick={close}
          sx={{}}
        >
          {t('addOneMore')}
        </Button>
      </div>
    </Dialog>
  );
}
